/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {
  useTheme,
  Stack,
  alpha,
  Button,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useLocalDatetime } from "hooks/useLocalDateTime";
import { useLanguage } from "hooks/settings";
import { HotkeyCompliantDataGrid } from "src/components/common/dataGrid";
import { NumberCell } from "src/components/common/dataGrid/NumberCell";
import { useNotifyStatus } from "contexts/status";
import { useFormatNumber } from "src/utils/formatNumber";
import {
  MinimumExceededError,
  ParseError,
  parseNumber,
} from "src/utils/parseNumber";
import { useMemo } from "react";
import { SupportedLanguage } from "src/store/api/generatedApi";
import { useInventoryMeasurementWithLocations } from "./InventoryMeasurementWithLocationsProvider";
import { InventoryMeasurementFormState } from "../types";
import { InventoryMeasurementSubmittedSummary } from "./InventoryMeasurementSubmittedSummary";

export const InventoryMeasurementWithLocationsForm = () => {
  const { t } = useTenantTranslation();
  const { inventoryMeasurementFormState } =
    useInventoryMeasurementWithLocations();

  const body = useMemo(() => {
    switch (inventoryMeasurementFormState) {
      case InventoryMeasurementFormState.Pending:
        return <InventoryMeasurementWithLocationsFormDesktop />;
      case InventoryMeasurementFormState.Submitting:
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              gap: 3,
            }}
          >
            <CircularProgress size={60} />
            <Typography variant="h6" color="text.secondary">
              {t("creatingRecipe")}
            </Typography>
          </Box>
        );
      case InventoryMeasurementFormState.Submitted:
        return <InventoryMeasurementSubmittedSummary />;
    }
  }, [inventoryMeasurementFormState, t]);

  return body;
};

export interface InventoryMeasurementWithLocationsRow {
  id: string; // material_name
  material_name: string;
  yard_quantity: number;
  yard_original_quantity: number;
  railcar_quantity: number;
  railcar_original_quantity: number;
  bay_estimate_quantity: number;
  bay_actual_quantity: number | null;
  specific_price: number;
  total: number;
}

export interface DeliveryMeasurementRow {
  id: string; // material_name
  material_name: string;
  quantity: number | null;
}

export const InventoryMeasurementWithLocationsFormDesktop = () => {
  const { t } = useTenantTranslation();
  const unitsFormatter = useUnitsFormatter(true);
  const theme = useTheme();
  const language = useLanguage();
  const { toLocalDatetime } = useLocalDatetime();
  const notifyStatus = useNotifyStatus();

  const {
    inventoryEstimate,
    inventoryBreakdown,
    inventoryMeasurementFormData,
    adjustQuantity,
    adjustYardQuantity,
    adjustRailcarQuantity,
    handleSubmit,
    inventoryMeasurementFormState,
    rollMillQuantity,
    setRollMillQuantity,
  } = useInventoryMeasurementWithLocations();

  const parseNumberLocalised = parseNumber(language as SupportedLanguage);
  const formatNumber = useFormatNumber();
  const QUANTITY_DECIMAL_PLACES = 2;
  const parseNumber2DP = parseNumberLocalised({
    decimalPlaces: QUANTITY_DECIMAL_PLACES,
    minimum: 0,
  });
  const formatNumber2DP = formatNumber(QUANTITY_DECIMAL_PLACES);
  const formatTime = (isoDate: string | null | undefined) => {
    if (!isoDate) return null;
    return toLocalDatetime(isoDate, "YYYY-MM-DD HH:mm");
  };

  const lastReportTime = formatTime(
    inventoryEstimate?.last_inventory_report_timestamp
  );
  const lastDeliveryTime = formatTime(
    inventoryEstimate?.latest_delivery_data_timestamp
  );

  // Process data
  const rows: InventoryMeasurementWithLocationsRow[] = useMemo(() => {
    if (!inventoryEstimate || !inventoryBreakdown) return [];

    const allMaterials = new Set<string>();
    inventoryEstimate.items.forEach((item) =>
      allMaterials.add(item.material_name)
    );

    const sapBreakdownItems = inventoryBreakdown.items.filter(
      (item) => item.source === "sap"
    );

    sapBreakdownItems.forEach((item) => allMaterials.add(item.material_name));

    const breakdownMap = new Map<
      string,
      { yard: number; railcar: number; price: number }
    >();
    sapBreakdownItems.forEach((item) => {
      if (!breakdownMap.has(item.material_name)) {
        breakdownMap.set(item.material_name, {
          yard: 0,
          railcar: 0,
          price: item.specific_price,
        });
      }
      const entry = breakdownMap.get(item.material_name);
      if (!entry) return;
      if (item.location === "Yard") entry.yard += item.quantity;
      if (item.location === "Rail Car") entry.railcar += item.quantity;
      if (item.specific_price) entry.price = item.specific_price;
    });

    const estimateMap = new Map(
      inventoryEstimate.items.map((item) => [
        item.material_name,
        { quantity: item.quantity ?? 0, price: item.specific_price ?? 0 },
      ])
    );

    const measurementMap = new Map(
      inventoryMeasurementFormData.items.map((item) => [
        item.material_name,
        {
          bay: item.adjusted_quantity,
          yard: item.adjusted_yard_quantity,
          railcar: item.adjusted_railcar_quantity,
        },
      ])
    );

    return Array.from(allMaterials).map((material) => {
      const breakdown = breakdownMap.get(material) ?? {
        yard: 0,
        railcar: 0,
        price: 0,
      };
      const estimate = estimateMap.get(material) ?? { quantity: 0, price: 0 };
      const adjustments = measurementMap.get(material);
      const bayActual = adjustments?.bay ?? null;
      const yardActual = adjustments?.yard ?? breakdown.yard;
      const railcarActual = adjustments?.railcar ?? breakdown.railcar;

      const bayToUse = bayActual ?? estimate.quantity;

      const price = breakdown.price || estimate.price;

      return {
        id: material,
        material_name: material,
        yard_quantity: yardActual,
        yard_original_quantity: breakdown.yard,
        railcar_quantity: railcarActual,
        railcar_original_quantity: breakdown.railcar,
        bay_estimate_quantity: estimate.quantity,
        bay_actual_quantity: bayActual,
        specific_price: price,
        total: yardActual + railcarActual + bayToUse,
      };
    });
  }, [inventoryEstimate, inventoryBreakdown, inventoryMeasurementFormData]);

  const deliveryRows: DeliveryMeasurementRow[] = useMemo(() => {
    if (!inventoryEstimate) return [];

    // Use the same list of materials as the inventory table
    const allMaterials = new Set<string>();
    inventoryEstimate.items.forEach((item) =>
      allMaterials.add(item.material_name)
    );
    if (inventoryBreakdown) {
      inventoryBreakdown.items
        .filter((item) => item.source === "sap")
        .forEach((item) => allMaterials.add(item.material_name));
    }

    // Map delivery quantities
    const deliveryMap = new Map(
      inventoryEstimate.items.map((item) => [
        item.material_name,
        item.deliveries_since_previous,
      ])
    );

    return Array.from(allMaterials)
      .sort()
      .map((material) => ({
        id: material,
        material_name: material,
        quantity: deliveryMap.get(material) ?? 0,
      }));
  }, [inventoryEstimate, inventoryBreakdown]);

  const rollMillRow: DeliveryMeasurementRow = useMemo(() => {
    return {
      id: "ROLL_MILL",
      material_name: t("rollMill"),
      quantity: rollMillQuantity,
    };
  }, [t, rollMillQuantity]);

  const deliveryTotalRow: DeliveryMeasurementRow = useMemo(() => {
    const totalQuantity = deliveryRows.reduce(
      (sum, row) => sum + (row.quantity ?? 0),
      0
    );
    return {
      id: "TOTAL",
      material_name: t("all"),
      quantity: totalQuantity,
    };
  }, [deliveryRows, t]);

  const totalRow: InventoryMeasurementWithLocationsRow = useMemo(() => {
    if (rows.length === 0) {
      return {
        id: "TOTAL",
        material_name: t("all"),
        yard_quantity: 0,
        yard_original_quantity: 0,
        railcar_quantity: 0,
        railcar_original_quantity: 0,
        bay_estimate_quantity: 0,
        bay_actual_quantity: null,
        specific_price: 0,
        total: 0,
      };
    }

    const sums = rows.reduce(
      (acc, row) => ({
        yard_quantity: acc.yard_quantity + row.yard_quantity,
        yard_original_quantity:
          acc.yard_original_quantity + row.yard_original_quantity,
        railcar_quantity: acc.railcar_quantity + row.railcar_quantity,
        railcar_original_quantity:
          acc.railcar_original_quantity + row.railcar_original_quantity,
        bay_estimate_quantity:
          acc.bay_estimate_quantity + row.bay_estimate_quantity,
        bay_actual_quantity:
          (acc.bay_actual_quantity ?? 0) + (row.bay_actual_quantity ?? 0),
        total: acc.total + row.total,
        hasBayActual: acc.hasBayActual || row.bay_actual_quantity !== null,
        weightedPriceSum: acc.weightedPriceSum + row.specific_price * row.total,
      }),
      {
        yard_quantity: 0,
        yard_original_quantity: 0,
        railcar_quantity: 0,
        railcar_original_quantity: 0,
        bay_estimate_quantity: 0,
        bay_actual_quantity: 0,
        total: 0,
        hasBayActual: false,
        weightedPriceSum: 0,
      }
    );

    return {
      id: "TOTAL",
      material_name: t("all"),
      yard_quantity: sums.yard_quantity,
      yard_original_quantity: sums.yard_original_quantity,
      railcar_quantity: sums.railcar_quantity,
      railcar_original_quantity: sums.railcar_original_quantity,
      bay_estimate_quantity: sums.bay_estimate_quantity,
      bay_actual_quantity: sums.hasBayActual ? sums.bay_actual_quantity : null,
      specific_price: sums.total ? sums.weightedPriceSum / sums.total : 0,
      total: sums.total,
    };
  }, [rows, t]);

  const handleRowUpdate = (
    newRow: InventoryMeasurementWithLocationsRow,
    oldRow: InventoryMeasurementWithLocationsRow
  ) => {
    if (newRow.bay_actual_quantity !== oldRow.bay_actual_quantity) {
      adjustQuantity(newRow.material_name, newRow.bay_actual_quantity);
    }
    if (newRow.yard_quantity !== oldRow.yard_quantity) {
      const yardValue =
        newRow.yard_quantity === newRow.yard_original_quantity
          ? null
          : newRow.yard_quantity;
      adjustYardQuantity(newRow.material_name, yardValue);
    }
    if (newRow.railcar_quantity !== oldRow.railcar_quantity) {
      const railcarValue =
        newRow.railcar_quantity === newRow.railcar_original_quantity
          ? null
          : newRow.railcar_quantity;
      adjustRailcarQuantity(newRow.material_name, railcarValue);
    }

    const bayToUse = newRow.bay_actual_quantity ?? newRow.bay_estimate_quantity;
    const newTotal = newRow.yard_quantity + newRow.railcar_quantity + bayToUse;
    return { ...newRow, total: newTotal };
  };

  const handleDeliveryRowUpdate = (newRow: DeliveryMeasurementRow) => {
    if (newRow.id === "ROLL_MILL") {
      setRollMillQuantity(newRow.quantity ?? 0);
    }
    return newRow;
  };

  const columns: GridColDef<InventoryMeasurementWithLocationsRow>[] = [
    {
      field: "material_name",
      headerName: t("material"),
      sortable: false,
      flex: 1.5,
      display: "flex",
    },
    {
      field: "bay_actual_quantity",
      headerName: "Bay Actual",
      sortable: false,
      editable: true,
      flex: 1,
      cellClassName: "numeric adjustment-input",
      valueSetter: (value: string, row) => {
        try {
          if (value) {
            const val = parseNumber2DP(value);
            return { ...row, bay_actual_quantity: val };
          } else {
            return { ...row, bay_actual_quantity: null };
          }
        } catch (e) {
          switch (true) {
            case e instanceof MinimumExceededError:
              notifyStatus({ text: t("invalidNumber"), type: "error" });
          }
          return row;
        }
      },
      valueGetter: (_: unknown, row) => {
        if (
          row.bay_actual_quantity !== null &&
          row.bay_actual_quantity !== undefined
        ) {
          return formatNumber(2)(row.bay_actual_quantity);
        }
        return null;
      },
      display: "flex",
    },
    {
      field: "bay_estimate_quantity",
      headerName: "Bay Estimate",
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <NumberCell
          decimalPlaces={2}
          value={params.row.bay_estimate_quantity}
        />
      ),
      display: "flex",
    },
    {
      field: "yard_quantity",
      headerName: "Yard",
      flex: 1,
      sortable: false,
      editable: true,
      cellClassName: "numeric",
      valueSetter: (value: string, row) => {
        try {
          if (value) {
            const val = parseNumber2DP(value);
            return { ...row, yard_quantity: val };
          } else {
            return { ...row, yard_quantity: row.yard_original_quantity };
          }
        } catch (e) {
          if (e instanceof MinimumExceededError || e instanceof ParseError) {
            notifyStatus({ text: t("invalidNumber"), type: "error" });
          }
          return row;
        }
      },
      valueGetter: (_: unknown, row) => formatNumber2DP(row.yard_quantity),
      display: "flex",
    },
    {
      field: "railcar_quantity",
      headerName: "Railcar",
      flex: 1,
      sortable: false,
      editable: true,
      cellClassName: "numeric",
      valueSetter: (value: string, row) => {
        try {
          if (value) {
            const val = parseNumber2DP(value);
            return { ...row, railcar_quantity: val };
          } else {
            return { ...row, railcar_quantity: row.railcar_original_quantity };
          }
        } catch (e) {
          if (e instanceof MinimumExceededError || e instanceof ParseError) {
            notifyStatus({ text: t("invalidNumber"), type: "error" });
          }
          return row;
        }
      },
      valueGetter: (_: unknown, row) => formatNumber2DP(row.railcar_quantity),
      display: "flex",
    },
    {
      field: "specific_price",
      headerName: `${t("price")}${unitsFormatter("specific_cost")}`,
      sortable: false,
      flex: 1,
      renderCell: (params) => (
        <NumberCell decimalPlaces={2} value={params.row.specific_price} />
      ),
      display: "flex",
    },
    {
      field: "total",
      headerName: "Total",
      sortable: false,
      flex: 1,
      renderCell: (params) => (
        <NumberCell decimalPlaces={2} value={params.row.total} />
      ),
      display: "flex",
    },
  ];

  const deliveryColumns: GridColDef<DeliveryMeasurementRow>[] = [
    {
      field: "material_name",
      headerName: t("material"),
      sortable: false,
      flex: 1.5,
      display: "flex",
    },
    {
      field: "quantity",
      headerName: t("quantityFromSap"),
      sortable: false,
      flex: 1,
      editable: true,
      valueSetter: (value: string, row) => {
        try {
          if (value) {
            const val = parseNumber2DP(value);
            return { ...row, quantity: val };
          } else {
            return { ...row, quantity: 0 };
          }
        } catch (e) {
          if (e instanceof MinimumExceededError) {
            notifyStatus({ text: t("invalidNumber"), type: "error" });
          }
          return row;
        }
      },
      renderCell: (params) => (
        <NumberCell decimalPlaces={2} value={params.row.quantity} />
      ),
      display: "flex",
    },
  ];

  if (!inventoryEstimate || !inventoryBreakdown) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack
      maxHeight="100%"
      sx={{ mb: 3, maxWidth: "80vw", mx: "auto" }}
      spacing={2}
    >
      <Typography variant="h6">
        {t("currentInventoryBreakdown")}
        {lastReportTime ? (
          <Typography
            component="span"
            variant="subtitle1"
            color="text.secondary"
            sx={{ ml: 2 }}
          >
            {t("asOf", { time: lastReportTime })}
          </Typography>
        ) : null}
      </Typography>
      <HotkeyCompliantDataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        processRowUpdate={handleRowUpdate}
        pinnedRows={{ bottom: [totalRow] }}
        isCellEditable={(params) =>
          (params.row as InventoryMeasurementWithLocationsRow).id !== "TOTAL"
        }
        getRowClassName={(params) => (params.id === "TOTAL" ? "total-row" : "")}
        initialState={{
          sorting: {
            sortModel: [{ field: "material_name", sort: "asc" }],
          },
        }}
        sx={{
          maxHeight: `60vh`, // Reduced to make space for deliveries
          "& .MuiDataGrid-cell--editing input": {
            textAlign: "right",
            paddingX: 1,
            ...theme.typography.body1Mono,
          },
          ".numeric": {
            display: "flex",
            justifyContent: "flex-end",
            ...theme.typography.body1Mono,
          },
          ".adjustment-input": {
            backgroundColor: alpha(theme.palette.data.blue, 0.1),
          },
          "& .total-row": {
            fontStyle: "italic",
            ...theme.typography.body1Mono,
          },
          "& .total-row:hover": {
            backgroundColor: theme.palette.action.hover,
          },
        }}
        cellSelection
        ignoreValueFormatterDuringExport
      />

      <Typography variant="h6" sx={{ mt: 2 }}>
        {t("deliveriesSinceLastSubmission")}
        {lastDeliveryTime ? (
          <Typography
            component="span"
            variant="subtitle1"
            color="text.secondary"
            sx={{ ml: 2 }}
          >
            {t("timeInterval", {
              start: lastDeliveryTime,
              end: lastReportTime ?? t("now"),
            })}
          </Typography>
        ) : null}
      </Typography>
      <HotkeyCompliantDataGrid
        rows={deliveryRows}
        columns={deliveryColumns}
        getRowId={(row) => row.id}
        processRowUpdate={handleDeliveryRowUpdate}
        isCellEditable={(params) =>
          (params.row as DeliveryMeasurementRow).id === "ROLL_MILL"
        }
        pinnedRows={{ top: [rollMillRow], bottom: [deliveryTotalRow] }}
        getRowClassName={(params) =>
          params.id === "TOTAL"
            ? "total-row"
            : params.id === "ROLL_MILL"
              ? "roll-mill-row"
              : ""
        }
        sx={{
          maxHeight: `30vh`,
          ".numeric": {
            display: "flex",
            justifyContent: "flex-end",
            ...theme.typography.body1Mono,
          },
          "& .total-row": {
            fontStyle: "italic",
            ...theme.typography.body1Mono,
          },
          "& .total-row:hover": {
            backgroundColor: theme.palette.action.hover,
          },
          "& .roll-mill-row": {
            backgroundColor: alpha(theme.palette.data.blue, 0.1),
          },
          "& .roll-mill-row:hover": {
            backgroundColor: alpha(theme.palette.data.blue, 0.2),
          },
        }}
        hideFooter
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={
            inventoryMeasurementFormState ===
            InventoryMeasurementFormState.Submitting
          }
          loading={
            inventoryMeasurementFormState ===
            InventoryMeasurementFormState.Submitting
          }
        >
          {t("submit")}
        </Button>
      </Box>
    </Stack>
  );
};
